import { splitDelimitedString } from '~/cynqueModules/componentHelpers/entityMaskForm/delimitedList'
import type {
  IAddressedEmailForPost,
  IEmailForPost,
  IEmailMessage,
} from '~/types/email/models'

/**
 * An email composer's values, as the form saves them: each recipient list ONE
 * comma-separated string (the fields' `delimitedStringSeparator`), or the
 * list itself.
 */
export interface IEmailFormValues {
  to?: unknown
  cc?: unknown
  bcc?: unknown
  subject?: string | null
  body?: string | null
}

/**
 * The IEmailForPost a composer's values make: each recipient list split into
 * addresses, and every key left out that holds nothing -- an omitted `to` or
 * `subject` is a choice the endpoint gives a meaning to (a registration's
 * Subject is its `descr`), where an empty one is a 400.
 */
export function emailForPostFromForm(values: IEmailFormValues): IEmailForPost {
  const to = splitDelimitedString(values.to)
  const cc = splitDelimitedString(values.cc)
  const bcc = splitDelimitedString(values.bcc)
  const subject = values.subject?.trim()
  return {
    body: values.body ?? '',
    ...(to.length ? { to } : {}),
    ...(cc.length ? { cc } : {}),
    ...(bcc.length ? { bcc } : {}),
    ...(subject ? { subject } : {}),
  }
}

/**
 * An IEmailForPost respelled as the IAddressedEmailForPost an email posted
 * with an entity takes: `to` / `cc` / `bcc` become `toAddresses` /
 * `ccAddresses` / `bccAddresses`, a list left out still left out.
 */
export function addressedEmailForPost({
  to,
  cc,
  bcc,
  ...rest
}: IEmailForPost & { to: string[] }): IAddressedEmailForPost {
  return {
    ...rest,
    toAddresses: to,
    ...(cc ? { ccAddresses: cc } : {}),
    ...(bcc ? { bccAddresses: bcc } : {}),
  }
}

/**
 * A stored message as a composer's values, to show it in that composer
 * read-only: the recipient lists as the comma-separated strings the fields
 * load, the text as `body`.
 */
export function emailMessageAsFormValues(
  message: IEmailMessage
): Required<IEmailFormValues> {
  const joined = (addresses: string[] | null | undefined) =>
    (addresses ?? []).join(', ')
  return {
    to: joined(message.toAddresses),
    cc: joined(message.ccAddresses),
    bcc: joined(message.bccAddresses),
    subject: message.subject,
    body: message.body ?? '',
  }
}
